/** Client-side resize + JPEG encode before secretbox + upload. */

/** Max plaintext bytes before secretbox (DM images/files and workspace blobs). */
export const MAX_ENCRYPTED_UPLOAD_BYTES = 50 * 1024 * 1024;

/** Max ciphertext size for POST /api/uploads (plain + secretbox MAC and padding slack). */
export const MAX_ENCRYPTED_CIPHER_BYTES = MAX_ENCRYPTED_UPLOAD_BYTES + 64;
const MAX_DIMENSION = 1280;
const JPEG_QUALITY = 0.82;

export type PreparedImage = {
  plainBytes: Uint8Array;
  width: number;
  height: number;
  mime: string;
};

const IMAGE_EXTENSIONS = new Set(
  ["jpg", "jpeg", "png", "gif", "webp", "bmp", "avif", "ico", "tif", "tiff", "heic", "heif"].map((s) =>
    s.toLowerCase(),
  ),
);

function extensionFromFilename(filename: string): string {
  const base = filename.replace(/^.*[/\\]/, "").trim();
  const dot = base.lastIndexOf(".");
  if (dot < 0 || dot === base.length - 1) return "";
  return base.slice(dot + 1).toLowerCase();
}

/** MIME image/* или известное расширение (часто пустой type на Windows). */
export function looksLikeImageFileForSend(file: File): boolean {
  const t = file.type.trim().toLowerCase();
  if (t.startsWith("image/")) return true;
  const ext = extensionFromFilename(file.name || "");
  return ext.length > 0 && IMAGE_EXTENSIONS.has(ext);
}

export async function prepareImageForSend(file: File): Promise<PreparedImage> {
  if (!looksLikeImageFileForSend(file)) {
    throw new Error("Выберите файл изображения");
  }
  const bmp = await createImageBitmap(file);
  let width = bmp.width;
  let height = bmp.height;
  if (width < 1 || height < 1) {
    bmp.close();
    throw new Error("Недопустимый размер изображения");
  }
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const scale = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    width = Math.max(1, Math.round(width * scale));
    height = Math.max(1, Math.round(height * scale));
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bmp.close();
    throw new Error("Canvas недоступен");
  }
  ctx.drawImage(bmp, 0, 0, width, height);
  bmp.close();

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", JPEG_QUALITY);
  });
  if (!blob) {
    throw new Error("Не удалось закодировать изображение");
  }
  const buf = new Uint8Array(await blob.arrayBuffer());
  return { plainBytes: buf, width, height, mime: "image/jpeg" };
}
